<!-- Cell Classification Modal - Dropdown for reclassifying cells -->
<template>
<div class="modal" :class="{ 'is-active': isOpen }" v-if="isOpen">
  <div class="modal-background" @click="closeModal"></div>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Cell Type Classification</p>
      <button class="delete" @click="closeModal" aria-label="close"></button>
    </header>
    
    <section class="modal-card-body">
      <div v-if="selectedCell" class="cell-info">
        <div class="cell-preview">
          <img 
            :src="selectedCell.thumbnailUrl || '/api/placeholder/120/120'" 
            :alt="`Cell ${selectedCell.index}`"
            class="cell-image"
          />
          <div class="cell-details">
            <p><strong>Cell #{{ selectedCell.index }}</strong></p>
            <p v-if="selectedCell.aiClassification">
              <span class="tag is-info is-small">AI: {{ selectedCell.aiClassification }}</span>
            </p>
            <p v-if="selectedCell.confidence">
              <span class="confidence">Confidence: {{ Math.round(selectedCell.confidence * 100) }}%</span>
            </p>
          </div>
        </div>
        
        <div class="classification-section">
          <div class="field">
            <label class="label">Select New Classification</label>
            <div class="control">
              <div class="select is-fullwidth">
                <select v-model="selectedClassification" @change="onClassificationChange">
                  <option value="">-- Select Classification --</option>
                  <optgroup 
                    v-for="category in classificationOptions" 
                    :key="category.name"
                    :label="category.name"
                  >
                    <option 
                      v-for="term in category.terms" 
                      :key="term.id"
                      :value="term.name"
                      :disabled="!term.isSupported"
                    >
                      {{ term.name }}{{ !term.isSupported ? ' *' : '' }}
                    </option>
                  </optgroup>
                </select>
              </div>
            </div>
            <p class="help" v-if="hasUnsupportedTypes">
              * Types marked with asterisk are not currently supported by AI preclassification
            </p>
          </div>
          
          <div v-if="selectedClassification" class="classification-preview">
            <p><strong>New Classification:</strong> {{ selectedClassification }}</p>
            <p v-if="currentClassification !== selectedClassification" class="change-indicator">
              <span class="tag is-warning is-small">
                {{ currentClassification || 'Unclassified' }} → {{ selectedClassification }}
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>
    
    <footer class="modal-card-foot">
      <button 
        class="button is-success" 
        @click="saveClassification"
        :disabled="!selectedClassification || selectedClassification === currentClassification"
        :class="{ 'is-loading': isSaving }"
      >
        Save changes
      </button>
      <button class="button" @click="closeModal">Close</button>
    </footer>
  </div>
</div>
</template>

<script>
export default {
  name: 'CellClassificationModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    selectedCell: {
      type: Object,
      default: null
    },
    classificationOptions: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'classify-cell'],
  
  data() {
    return {
      selectedClassification: '',
      isSaving: false
    };
  },
  
  computed: {
    currentClassification() {
      return this.selectedCell?.userClassification || this.selectedCell?.aiClassification || '';
    },
    
    hasUnsupportedTypes() {
      return this.classificationOptions.some(category => 
        category.terms.some(term => !term.isSupported)
      );
    }
  },
  
  watch: {
    selectedCell: {
      handler(newCell) {
        if (newCell) {
          this.selectedClassification = this.currentClassification;
        }
      },
      immediate: true
    },
    
    isOpen(newValue) {
      if (newValue && this.selectedCell) {
        this.selectedClassification = this.currentClassification;
        this.isSaving = false;
      }
    }
  },
  
  methods: {
    closeModal() {
      this.selectedClassification = '';
      this.isSaving = false;
      this.$emit('close');
    },
    
    onClassificationChange() {
      // Optional: Add logic for classification change validation
    },
    
    async saveClassification() {
      if (!this.selectedClassification || !this.selectedCell) return;
      
      this.isSaving = true;
      
      try {
        await this.$emit('classify-cell', {
          cellId: this.selectedCell.id,
          oldClassification: this.currentClassification,
          newClassification: this.selectedClassification
        });
        
        this.closeModal();
        
      } catch (error) {
        console.error('Failed to save classification:', error);
        this.$notify({
          type: 'error',
          text: 'Failed to save classification. Please try again.'
        });
      } finally {
        this.isSaving = false;
      }
    }
  }
};
</script>

<style scoped>
.cell-info {
  padding: 1rem 0;
}

.cell-preview {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #dee2e6;
}

.cell-image {
  width: 120px;
  height: 120px;
  object-fit: cover;
  border-radius: 4px;
  border: 2px solid #007bff;
  background: #f8f9fa;
}

.cell-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cell-details p {
  margin: 0;
  font-size: 0.9rem;
}

.confidence {
  color: #6c757d;
  font-size: 0.85rem;
}

.classification-section {
  margin-top: 1rem;
}

.classification-preview {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e8f5e8;
  border-radius: 4px;
  border-left: 4px solid #28a745;
}

.classification-preview p {
  margin: 0;
  font-size: 0.9rem;
}

.change-indicator {
  margin-top: 0.5rem;
}

.tag.is-info.is-small {
  background: #007bff;
  color: white;
}

.tag.is-warning.is-small {
  background: #ffc107;
  color: #212529;
}

/* Ensure modal appears above other content */
.modal {
  z-index: 9999;
}

.modal-card {
  max-width: 500px;
  margin: 0 auto;
}

.select select option:disabled {
  color: #6c757d;
  font-style: italic;
}
</style>